<script setup lang="ts">
/**
 * Список встреч — левая колонка экрана «Встречи» (макеты 10-1…10-4).
 *
 * Раскладка мессенджера: слева разговоры, справа их содержимое. Встреча — это и
 * есть разговор, и искать вчерашний протокол человек идёт туда же, где искал бы
 * вчерашнюю переписку.
 *
 * Первой строкой — личные заметки: они не встреча, но лежат в той же ленте и
 * пишутся тем же полем. Отдельная страница ради них означала бы два места, где
 * ищут одну и ту же мысль.
 */
import { NotebookPen, Users } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/features/auth/store'

import { formatNoteStamp } from '../notes'

import type { MeetingSession } from '../api'

const props = defineProps<{
  meetings: MeetingSession[]
  /** Открытая встреча; пусто — открыты личные заметки */
  selectedId: string
  isLoading: boolean
}>()

const emit = defineEmits<{ select: [sessionId: string] }>()

const { t, locale } = useI18n()
const auth = useAuthStore()

/** Идущая встреча всегда сверху: за ней и приходят */
const live = computed(() => props.meetings.filter((meeting) => meeting.active))
const past = computed(() => props.meetings.filter((meeting) => !meeting.active))

const stamp = (meeting: MeetingSession) =>
  formatNoteStamp(meeting.createdAt, locale.value, {
    today: t('meetings.today'),
    yesterday: t('meetings.yesterday'),
  })

/**
 * Подпись строки — кто был, без себя самого.
 *
 * «Ты, Анна, Иван» на каждой строке — это три четверти списка, занятые словом,
 * которое одинаково везде: человек и так знает, что был на своей встрече.
 */
const people = (meeting: MeetingSession) =>
  meeting.participants
    .filter((participant) => participant.joined && participant.userId !== auth.user?.id)
    .map((participant) => participant.name)
    .filter(Boolean)
    .join(', ')
</script>

<template>
  <aside class="mlist">
    <button
      type="button"
      class="mlist__item"
      :class="{ 'mlist__item--selected': !selectedId }"
      @click="emit('select', '')"
    >
      <NotebookPen :size="16" class="mlist__icon" aria-hidden="true" />

      <span class="mlist__body">
        <span class="mlist__title">{{ t('meetings.personal.title') }}</span>
        <span class="mlist__sub">{{ t('meetings.personal.hint') }}</span>
      </span>
    </button>

    <p v-if="isLoading && !meetings.length" class="mlist__empty">{{ t('meetings.loading') }}</p>

    <template v-for="group in [live, past]" :key="group.length">
      <button
        v-for="meeting in group"
        :key="meeting.id"
        type="button"
        class="mlist__item"
        :class="{ 'mlist__item--selected': selectedId === meeting.id }"
        @click="emit('select', meeting.id)"
      >
        <Users :size="16" class="mlist__icon" aria-hidden="true" />

        <span class="mlist__body">
          <span class="mlist__title">
            {{ t('meetings.item.title') }}
            <!-- Точка у идущей встречи: по ней её находят глазом, не читая. -->
            <span v-if="meeting.active" class="mlist__live" :title="t('meetings.item.live')" />
          </span>

          <span class="mlist__sub">
            {{ people(meeting) || t('meetings.item.alone') }}
          </span>
        </span>

        <span class="mlist__meta">
          <span class="mlist__stamp">{{ stamp(meeting) }}</span>
          <span v-if="meeting.noteCount" class="mlist__count">{{ meeting.noteCount }}</span>
        </span>
      </button>
    </template>

    <p v-if="!isLoading && !meetings.length" class="mlist__empty">{{ t('meetings.list.empty') }}</p>
  </aside>
</template>

<style scoped>
.mlist {
  display: flex;
  flex: 0 0 300px;
  flex-direction: column;
  gap: 2px;
  width: 300px;
  /* Список скроллится внутри себя: длинная история созвонов не должна тянуть
     вниз ленту справа — ровно та беда, из-за которой заметки уехали с плана. */
  min-height: 0;
  overflow-y: auto;
  padding-right: 8px;
  border-right: 1px solid var(--color-border);
}

.mlist__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.mlist__item:hover {
  background: var(--color-bg-muted);
}

.mlist__item--selected {
  background: var(--color-primary-tint);
}

.mlist__icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--color-text-muted);
}

.mlist__item--selected .mlist__icon {
  color: var(--color-primary);
}

.mlist__body {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.mlist__title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  line-height: 18px;
}

.mlist__live {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-success-strong);
}

.mlist__sub {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mlist__meta {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.mlist__stamp {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  line-height: 14px;
}

.mlist__count {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: var(--font-size-overline);
  line-height: 16px;
  text-align: center;
}

.mlist__empty {
  margin: 8px 10px;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}
</style>
